//TODO : Refactor after ai
/**
 * Action-Leiste und Statusleiste für die Datenansicht: Jede Rolle sieht an
 * jedem Datensatz nur die Actions, die ihr im aktuellen Zustand zustehen –
 * Zustandswechsel, Folge-Actions (Kalkulation rechnen, Angebot erstellen …),
 * Bearbeiten, Zuordnen, Bestandteile anlegen (Positionen, Zeilen, Fotos).
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { personaVon, useDatenStore } from "@app/store";
import {
  anlegeAktion,
  eingehendeRelationen,
  erlaubteAktionen,
  folgeAktionen,
  objektKlasse,
  type Aktion,
  type Datensatz,
  type ObjektKlasse,
} from "@shared/demo-data/viacid";
import { ch } from "./lib";
import { Knopf } from "./ui";
import { BeziehungDialog, SatzFormular } from "./Dialoge";
import { bedienungVon, useStatusAktion } from "./aktionen";

type Offen =
  | {
      art: "formular";
      klasse: ObjektKlasse;
      aktion: Aktion;
      satz?: Datensatz;
      vorbelegung?: Record<string, string>;
    }
  | { art: "beziehung"; aktion: Aktion };

export function Statusleiste({
  klasse,
  satz,
}: {
  klasse: ObjektKlasse;
  satz: Datensatz;
}) {
  if (klasse.zustaende.length === 0) return null;
  const i = klasse.zustaende.indexOf(satz.status ?? "");
  return (
    <div className="vc-status">
      {klasse.zustaende.map((z, n) => (
        <span
          key={z}
          className={`vc-status__step ${n < i ? "past" : ""} ${n === i ? "now" : ""}`}
        >
          {ch(z)}
        </span>
      ))}
    </div>
  );
}

export function ActionLeiste({
  klasse,
  satz,
  ausser = [],
  verwandt = [],
}: {
  klasse: ObjektKlasse;
  satz: Datensatz;
  /** Actions (Name) oder Folge-Actions (Key), die der Modul-Screen bereits als eigenen Knopf zeigt. */
  ausser?: string[];
  /** Objekte, die von hier aus angelegt werden (z. B. Kundenkontakt zum Kunden), mit der vorbelegten Relation. */
  verwandt?: { slug: string; rel: string; mit?: Record<string, string> }[];
}) {
  const nav = useNavigate();
  const bestand = useDatenStore((s) => s.bestand);
  const rolle = useDatenStore((s) => s.rolle);
  const anwenden = useDatenStore((s) => s.anwenden);
  const statusAktion = useStatusAktion();
  const [offen, setOffen] = useState<Offen | null>(null);

  const aktionen = [...erlaubteAktionen(klasse, satz, rolle)]
    .filter((a) => !ausser.includes(a.name))
    .sort(
      (a, b) =>
        Number(a.ziel == null) - Number(b.ziel == null) ||
        a.prioritaet - b.prioritaet
    );
  const folge = folgeAktionen(bestand, klasse.slug, satz, rolle).filter(
    (f) => !ausser.includes(f.key)
  );
  const verwandte = verwandt
    .map((v) => ({ ...v, k: objektKlasse(v.slug) }))
    .filter((v): v is typeof v & { k: ObjektKlasse } => v.k != null)
    .map((v) => ({ ...v, aktion: anlegeAktion(v.k, rolle) }))
    .filter((v) => v.aktion);
  // Bestandteile, die hier entstehen: Positionen, Zeilen, Fotos
  const bestandteile = eingehendeRelationen(klasse.slug)
    .filter((r) => !r.zuViele)
    .map((r) => ({ rel: r, k: objektKlasse(r.von) }))
    .filter(
      (x): x is { rel: (typeof x)["rel"]; k: ObjektKlasse } =>
        x.k != null && (!x.k.inNavigation || x.k.slug === "baustellenfoto")
    )
    .map((x) => ({ ...x, aktion: anlegeAktion(x.k, rolle) }))
    .filter((x) => x.aktion);
  const chanceKlasse = objektKlasse("verkaufschance");
  const chanceAktion =
    klasse.slug === "kunde" && chanceKlasse
      ? anlegeAktion(chanceKlasse, rolle)
      : undefined;

  const ausfuehren = (a: Aktion) => {
    const art = bedienungVon(a);
    if (art === "formular")
      setOffen({ art: "formular", klasse, aktion: a, satz });
    else if (art === "beziehung") setOffen({ art: "beziehung", aktion: a });
    else statusAktion(klasse, satz, a);
  };

  const leer =
    folge.length + aktionen.length + bestandteile.length + verwandte.length ===
      0 && !chanceAktion;

  return (
    <>
      <div className="vc-actions">
        {folge.map((f, i) => (
          <Knopf
            key={f.key}
            art={i === 0 ? "primary" : undefined}
            onClick={() => {
              const e = f.ausfuehren(bestand, satz, personaVon(rolle));
              anwenden(e);
              if (e.oeffnen && !e.fehler)
                nav(`/app/daten/${e.oeffnen.slug}/${e.oeffnen.id}`);
            }}
          >
            {ch(f.label)}
          </Knopf>
        ))}
        {aktionen.map((a, i) => (
          <Knopf
            key={a.name}
            art={folge.length === 0 && i === 0 ? "primary" : undefined}
            onClick={() => ausfuehren(a)}
          >
            {ch(a.name)}
          </Knopf>
        ))}
        {chanceAktion && chanceKlasse && (
          <Knopf
            onClick={() =>
              setOffen({
                art: "formular",
                klasse: chanceKlasse,
                aktion: chanceAktion,
                vorbelegung: { "verkaufschance-betrifft-kunde": satz.id },
              })
            }
          >
            Verkaufschance anlegen
          </Knopf>
        )}
        {verwandte.map(({ rel, k, aktion, mit }) => (
          <Knopf
            key={"v-" + rel}
            art="ghost"
            onClick={() =>
              setOffen({
                art: "formular",
                klasse: k,
                aktion: aktion as Aktion,
                vorbelegung: { [rel]: satz.id, ...mit },
              })
            }
          >
            + {ch((aktion as Aktion).name)}
          </Knopf>
        ))}
        {bestandteile.map(({ rel, k, aktion }) => (
          <Knopf
            key={rel.id}
            art="ghost"
            onClick={() =>
              setOffen({
                art: "formular",
                klasse: k,
                aktion: aktion as Aktion,
                vorbelegung: { [rel.id]: satz.id },
              })
            }
          >
            + {ch((aktion as Aktion).name)}
          </Knopf>
        ))}
        {leer && ausser.length === 0 && (
          <span className="sub">Für diese Rolle hier nichts zu tun</span>
        )}
      </div>
      {offen?.art === "formular" && (
        <SatzFormular
          klasse={offen.klasse}
          aktion={offen.aktion}
          satz={offen.satz}
          vorbelegung={offen.vorbelegung}
          onClose={(neu) => {
            setOffen(null);
            if (neu && offen.klasse.slug !== klasse.slug)
              nav(`/app/daten/${offen.klasse.slug}/${neu.id}`);
          }}
        />
      )}
      {offen?.art === "beziehung" && (
        <BeziehungDialog
          klasse={klasse}
          aktion={offen.aktion}
          satz={satz}
          onClose={() => setOffen(null)}
        />
      )}
    </>
  );
}

/** „Anlegen“ oben auf einer Liste, wenn die Rolle das darf. */
export function AnlegenKnopf({ klasse }: { klasse: ObjektKlasse }) {
  const nav = useNavigate();
  const rolle = useDatenStore((s) => s.rolle);
  const [offen, setOffen] = useState(false);
  const aktion = anlegeAktion(klasse, rolle);
  if (!aktion) return null;
  return (
    <>
      <Knopf art="primary" onClick={() => setOffen(true)}>
        + {ch(aktion.name)}
      </Knopf>
      {offen && (
        <SatzFormular
          klasse={klasse}
          aktion={aktion}
          onClose={(neu) => {
            setOffen(false);
            if (neu) nav(`/app/daten/${klasse.slug}/${neu.id}`);
          }}
        />
      )}
    </>
  );
}
